import { lazy, useCallback, useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import RequireAuth from './components/auth/RequireAuth';
import TermsAgreementGate from './components/auth/TermsAgreementGate';
import DashboardLayout from './components/layout/DashboardLayout';
import Layout from './components/layout/Layout';
import { useAuth } from './context/AuthContext';
import { OnboardingProvider } from './context/OnboardingContext';
import { initPushNotifications, setStatusBarStyle, useAppUrlOpen } from './hooks/useNativeFeatures';
import { PATHS } from './routes/paths';

const HomePage = lazy(() => import('./pages/HomePage'));
const VendorsPage = lazy(() => import('./pages/VendorsPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const VendorDetailPage = lazy(() => import('./pages/VendorDetailPage'));
const ListYourServicePage = lazy(() => import('./pages/ListYourServicePage'));
const EarnWithVgoPage = lazy(() => import('./pages/EarnWithVgoPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const SignupCustomerPage = lazy(() => import('./pages/SignupCustomerPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ClaimPage = lazy(() => import('./pages/ClaimPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentCancelPage = lazy(() => import('./pages/PaymentCancelPage'));
const MapPage = lazy(() => import('./pages/MapPage'));
const BudgetBuilderPage = lazy(() => import('./pages/BudgetBuilderPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const EventPage = lazy(() => import('./pages/EventPage'));
const EventOrderPage = lazy(() => import('./pages/EventOrderPage'));
const PickupOrderPage = lazy(() => import('./pages/PickupOrderPage'));
const EventVendorProfilePage = lazy(() => import('./pages/EventVendorProfilePage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const OnboardingLayout = lazy(() => import('./pages/onboarding/OnboardingLayout'));
const BusinessInfo = lazy(() => import('./pages/onboarding/BusinessInfo'));
const ServiceDetails = lazy(() => import('./pages/onboarding/ServiceDetails'));
const Photos = lazy(() => import('./pages/onboarding/Photos'));
const Pricing = lazy(() => import('./pages/onboarding/Pricing'));
const LocationStep = lazy(() => import('./pages/onboarding/LocationStep'));
const Availability = lazy(() => import('./pages/onboarding/Availability'));
const PaymentSetup = lazy(() => import('./pages/onboarding/PaymentSetup'));

const AdminSeedPage = lazy(() => import('./pages/AdminSeedPage'));
const AdminMonitorPage = lazy(() => import('./pages/AdminMonitorPage'));
const AdminClaimRequestsPage = lazy(() => import('./pages/AdminClaimRequestsPage'));
const AdminVendorsPage = lazy(() => import('./pages/AdminVendorsPage'));
const AdminDiscoveryPage = lazy(() => import('./pages/AdminDiscoveryPage'));
const VendorOverview = lazy(() => import('./pages/dashboard/vendor/Overview'));
const VendorListings = lazy(() => import('./pages/dashboard/vendor/Listings'));
const VendorAvailability = lazy(() => import('./pages/dashboard/vendor/Availability'));
const VendorBookings = lazy(() => import('./pages/dashboard/vendor/Bookings'));
const VendorPayments = lazy(() => import('./pages/dashboard/vendor/Payments'));
const VendorSettings = lazy(() => import('./pages/dashboard/vendor/Settings'));
const VendorOrders = lazy(() => import('./pages/dashboard/vendor/Orders'));
const VendorCredentials = lazy(() => import('./pages/dashboard/vendor/Credentials'));
const VendorApplications = lazy(() => import('./pages/dashboard/vendor/Applications'));
const VendorApplyToEvent = lazy(() => import('./pages/dashboard/vendor/ApplyToEvent'));
const CustomerOverview = lazy(() => import('./pages/dashboard/customer/Overview'));
const CustomerBookings = lazy(() => import('./pages/dashboard/customer/Bookings'));
const CustomerFavorites = lazy(() => import('./pages/dashboard/customer/Favorites'));
const CustomerOrders = lazy(() => import('./pages/dashboard/customer/Orders'));
const CustomerEvents = lazy(() => import('./pages/dashboard/customer/Events'));
const CustomerEventApplications = lazy(() => import('./pages/dashboard/customer/EventApplications'));
const CustomerSettings = lazy(() => import('./pages/dashboard/customer/Settings'));

/** /dashboard sends each role to the right home. */
function DashboardRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to={PATHS.login} replace />;
  if (user.role === 'admin') return <Navigate to="/admin/vendors" replace />;
  return <Navigate to={user.role === 'vendor' ? PATHS.vendorDashboard : PATHS.customerDashboard} replace />;
}

/** Capacitor bootstrap: status bar, push notifications, deep links. No-ops on web. */
function NativeBootstrap() {
  const navigate = useNavigate();

  useEffect(() => {
    setStatusBarStyle('dark');
    initPushNotifications();
  }, []);

  const handleAppUrlOpen = useCallback((url) => {
    try {
      const parsed = new URL(url);
      const path = parsed.pathname + parsed.search;
      if (path) navigate(path);
    } catch {
      // Not a parseable deep link - ignore.
    }
  }, [navigate]);

  useAppUrlOpen(handleAppUrlOpen);

  return null;
}

export default function App() {
  return (
    <>
      <NativeBootstrap />
      <Analytics />
      <TermsAgreementGate />
      <Routes>
        <Route element={<Layout />}>
          {/* Public */}
          <Route path={PATHS.home} element={<HomePage />} />
          <Route path={PATHS.vendors} element={<VendorsPage />} />
          <Route path="/vendors/:category" element={<VendorsPage />} />
          <Route path="/vendor/:vendorId" element={<VendorDetailPage />} />
          <Route path={PATHS.rentals} element={<VendorsPage forcedCategory="rentals" />} />
          <Route path={PATHS.catering} element={<VendorsPage forcedCategory="catering" />} />
          <Route path={PATHS.services} element={<ServicesPage />} />
          <Route path={PATHS.listYourService} element={<ListYourServicePage />} />
          <Route path={PATHS.earnWithVgo} element={<EarnWithVgoPage />} />
          <Route path={PATHS.map} element={<MapPage />} />
          <Route path={PATHS.budgetBuilder} element={<BudgetBuilderPage />} />
          <Route path={PATHS.events} element={<EventsPage />} />
          <Route path="/events/:slug" element={<EventPage />} />
          <Route path="/events/:slug/order/:truckId" element={<EventOrderPage />} />
          <Route path="/order/:vendorId" element={<PickupOrderPage />} />
          <Route path="/events/:slug/vendor/:truckId" element={<EventVendorProfilePage />} />
          <Route path={PATHS.privacy} element={<PrivacyPage />} />
          <Route path={PATHS.terms} element={<TermsPage />} />
          <Route path={PATHS.support} element={<SupportPage />} />

          {/* Auth */}
          <Route path={PATHS.signup} element={<SignupPage />} />
          <Route path={PATHS.signupCustomer} element={<SignupCustomerPage />} />
          {/* Legacy vendor signup URL - accounts are unified now */}
          <Route path="/signup/vendor" element={<Navigate to={PATHS.signupForListing} replace />} />
          <Route path={PATHS.login} element={<LoginPage />} />
          <Route path="/claim/:token" element={<ClaimPage />} />
          <Route path={PATHS.forgotPassword} element={<ForgotPasswordPage />} />
          <Route path={PATHS.resetPassword} element={<ResetPasswordPage />} />

          {/* Messaging */}
          <Route
            path={PATHS.messages}
            element={
            <RequireAuth>
                <MessagesPage />
              </RequireAuth>
            } />


          {/* Vendor onboarding - business info only; the account already exists */}
          <Route
            path={PATHS.onboarding}
            element={
            <RequireAuth>
                <OnboardingLayout />
              </RequireAuth>
            }>

            <Route index element={<Navigate to={PATHS.onboardingBusinessInfo} replace />} />
            <Route path="business-info" element={<BusinessInfo />} />
            <Route path="service-details" element={<ServiceDetails />} />
            <Route path="photos" element={<Photos />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="location" element={<LocationStep />} />
            <Route path="availability" element={<Availability />} />
            <Route path="payment" element={<PaymentSetup />} />
          </Route>

          {/* Dashboards */}
          <Route path={PATHS.dashboard} element={<DashboardRedirect />} />
          <Route
            path={PATHS.vendorDashboard}
            element={
            <RequireAuth role="vendor">
                <DashboardLayout role="vendor" />
              </RequireAuth>
            }>

            <Route index element={<VendorOverview />} />
            <Route path="listings" element={<VendorListings />} />
            <Route path="availability" element={<VendorAvailability />} />
            <Route path="bookings" element={<VendorBookings />} />
            <Route path="orders" element={<VendorOrders />} />
            <Route path="credentials" element={<VendorCredentials />} />
            <Route path="applications" element={<VendorApplications />} />
            <Route path="apply/:eventId" element={<VendorApplyToEvent />} />
            <Route path="payments" element={<VendorPayments />} />
            <Route path="settings" element={<VendorSettings />} />
          </Route>
          <Route
            path={PATHS.customerDashboard}
            element={
            <RequireAuth role="customer">
                <DashboardLayout role="customer" />
              </RequireAuth>
            }>

            <Route index element={<CustomerOverview />} />
            <Route path="bookings" element={<CustomerBookings />} />
            <Route path="orders" element={<CustomerOrders />} />
            <Route path="events" element={<CustomerEvents />} />
            <Route path="events/:eventId/applications" element={<CustomerEventApplications />} />
            <Route path="favorites" element={<CustomerFavorites />} />
            <Route path="settings" element={<CustomerSettings />} />
          </Route>

          {/* Payments */}
          <Route
            path="/checkout/:listingId"
            element={
            <RequireAuth>
                <CheckoutPage />
              </RequireAuth>
            } />

          <Route path={PATHS.paymentSuccess} element={<PaymentSuccessPage />} />
          <Route path={PATHS.paymentCancel} element={<PaymentCancelPage />} />

          {/* Internal admin tooling (backend enforces admin authorization) */}
          <Route
            path="/admin/seed"
            element={
            <RequireAuth role="admin">
                <AdminSeedPage />
              </RequireAuth>
            } />
          <Route
            path={PATHS.adminMonitor}
            element={
            <RequireAuth role="admin">
                <AdminMonitorPage />
              </RequireAuth>
            } />

          <Route
            path="/admin/claim-requests"
            element={
            <RequireAuth role="admin">
                <AdminClaimRequestsPage />
              </RequireAuth>
            } />

          <Route
            path="/admin/vendors"
            element={
            <RequireAuth role="admin">
                <AdminVendorsPage />
              </RequireAuth>
            } />

          <Route
            path="/admin/discovery"
            element={
            <RequireAuth role="admin">
                <AdminDiscoveryPage />
              </RequireAuth>
            } />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </>);

}

/** Providers that need router context are composed in index.js; Onboarding is provider-wrapped here. */
export function AppWithProviders() {
  return (
    <OnboardingProvider>
      <App />
    </OnboardingProvider>);

}
